// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { LocalAudioStream, MediaClient, MediaSessionAgent, MediaStreamSession, RemoteStream } from '@skype/spool-sdk';
import { fetchCredentialsForACSIdentity } from './joinMeeting';
import EventEmitter from 'events';
import { BotServerAppInsights, callWithEventTracking } from './BotServerAppInsights';
import { fetchWithDetailedTiming } from './fetchWithDetailedTiming';

export interface BotSessionData {
  sessionId: string;
  serviceOriginUrl: string;
  configureUrl: string;
  healthCheckUrl?: string;
}

export const triggerNewBotSession = async (
  functionsAppToken: string,
  appInsights?: BotServerAppInsights
): Promise<BotSessionData> => {
  const response = await fetchWithDetailedTiming(
    '/api/newBotSession',
    {
      headers: new Headers({
        Authorization: `Bearer ${functionsAppToken}`
      }),
      method: 'POST',
      body: JSON.stringify({ operationId: appInsights?.operationId })
    },
    appInsights,
    'BotSession_TriggerNewBotSession'
  );
  if (!response.ok) {
    throw new Error('Failed to get sessionId for bot');
  }

  return response.json();
};

export const endBotSession = async (sessionId: string, functionsAppToken: string): Promise<void> => {
  if (!sessionId) {
    return;
  }

  const response = await fetch('/api/endBotSession', {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${functionsAppToken}`
    }),
    body: JSON.stringify({ sessionId })
  });

  if (!response.ok) {
    console.error('Failed call /api/endBotSession', response.statusText);
  }

  return;
};

export class SpeakToBotSessionManager {
  private botSessionData: BotSessionData;
  private functionsAppToken: string;
  private mediaClient: MediaClient = new MediaClient();
  private mediaSessionAgent: MediaSessionAgent;
  private session?: MediaStreamSession;
  private localAudioStream?: LocalAudioStream;
  private healthCheckInterval?: number;
  private readonly healthCheckIntervalTime = 3000; // 3 seconds
  private eventEmitter = new EventEmitter();
  private appInsights?: BotServerAppInsights;

  static create = async (
    functionsAppToken: string,
    botSessionData: BotSessionData,
    appInsights?: BotServerAppInsights
  ): Promise<SpeakToBotSessionManager> => {
    const credentials = await fetchCredentialsForACSIdentity(functionsAppToken);
    const mediaClient = callWithEventTracking(() => new MediaClient(), appInsights, 'BotSession_MediaClient_Create');
    const mediaSessionAgent = await callWithEventTracking(
      async () =>
        await mediaClient.createSessionAgent(credentials.tokenCredential, {
          serviceOrigin: botSessionData.serviceOriginUrl
        }),
      appInsights,
      'BotSession_MediaSessionAgent_Create'
    );

    return new SpeakToBotSessionManager(mediaClient, mediaSessionAgent, botSessionData, functionsAppToken, appInsights);
  };

  private constructor(
    mediaClient: MediaClient,
    mediaSessionAgent: MediaSessionAgent,
    botSessionData: BotSessionData,
    functionsAppToken: string,
    appInsights?: BotServerAppInsights
  ) {
    this.mediaClient = mediaClient;
    this.mediaSessionAgent = mediaSessionAgent;
    this.botSessionData = botSessionData;
    this.functionsAppToken = functionsAppToken;
    this.appInsights = appInsights;
    window.addEventListener('beforeunload', this.disconnect);
  }

  private configureRequest = async (request: unknown): Promise<void> => {
    console.log('SessionManager::on(configureRequest)', request);
    const response = await fetch(this.botSessionData.configureUrl, {
      method: 'POST',
      body: JSON.stringify(request)
    });
    console.log('SessionManager::on(configureRequest) response', response.status, response.statusText);
    if (!response.ok) {
      this.eventEmitter.emit('serverError', 'configureRequest failed');
      throw new Error(`Failed to configure bot session: ${response.status} ${response.statusText}`);
    }
  };

  private sessionStateChanged = (): void => {
    console.log('SessionManager::on(sessionStateChanged)', this.session?.state);
  };

  private streamsUpdated = (e: { added: RemoteStream[]; removed: RemoteStream[] }): void => {
    console.log('SessionManager::on(streamsUpdated)', e);
    this.appInsights?.trackEvent({
      name: 'BotSession_StreamUpdated',
      properties: {
        totalRemoteAudioStreams: this.session?.remoteAudioStreams
      }
    });
  };

  public connect = async (): Promise<void> => {
    if (this.session) {
      console.log('SessionManager::Already connected');
      return;
    }

    try {
      const dm = await this.mediaClient.getDeviceManager();
      const result = await dm.askDevicePermission({
        audio: true,
        video: false
      });

      if (!result.audio) {
        throw new Error('Microphone permission not granted!');
      }

      const start = performance.now();

      this.appInsights?.trackEventStart('BotSession_Connect');
      const availableMics = await dm.getMicrophones();

      if (availableMics.length === 0) {
        throw new Error('No microphones found!');
      }
      dm.selectMicrophone(availableMics[0]);

      this.session = await callWithEventTracking(
        async () =>
          await this.mediaSessionAgent.joinSession({
            sessionId: this.botSessionData.sessionId
          }),
        this.appInsights,
        'BotSession_JoinSession'
      );
      const timeTaken = performance.now() - start;
      console.log(`SessionManager::JoinSession Completed (${timeTaken | 0}ms), waiting for configureRequest...`);

      this.session.on('stateChanged', this.sessionStateChanged);
      this.session.on('streamsUpdated', this.streamsUpdated);
      const configureRequestPromise = new Promise<void>((resolve, reject) => {
        let timeout: number | undefined = undefined;

        const onConfigureRequest = async (request: unknown): Promise<void> => {
          clearTimeout(timeout);
          await callWithEventTracking(
            async () => this.configureRequest(request),
            this.appInsights,
            'BotSession_ConfigureRequest'
          );
          this.session?.off('configureRequest', this.configureRequest);
          resolve();
        };

        timeout = window.setTimeout(() => {
          this.appInsights?.trackEventFail('BotSession_ConfigureRequest', {
            reason: 'timeout'
          });
          this.session?.off('configureRequest', onConfigureRequest);
          reject(new Error('Timeout waiting for configureRequest event'));
        }, 10000);

        this.session?.on('configureRequest', onConfigureRequest);
      });

      await configureRequestPromise;

      console.log(`SessionManager::Connected (${(performance.now() - start) | 0}ms)}`);
      this.appInsights?.trackEventSuccess('BotSession_Connect');
    } catch (e) {
      this.appInsights?.trackEventFail('BotSession_Connect');
      throw e;
    }

    // TODO: Ideally we change this check with a streams.removed event to detect when the bot is disconnected
    this.healthCheckInterval = window.setInterval(async () => {
      if (!this.botSessionData.healthCheckUrl) {
        return;
      }

      let success = false;
      try {
        const response = await fetch(this.botSessionData.healthCheckUrl, {
          method: 'GET'
        });
        success = response.ok;
      } catch (e) {
        success = false;
      }

      if (!success) {
        this.eventEmitter.emit('serverError', 'healthcheck failed');
        clearInterval(this.healthCheckInterval);
        this.appInsights?.trackEvent({
          name: 'BotSession_ServerCrash',
          properties: {
            message: 'Bot server down'
          }
        });
        this.disconnect();
      } else {
        console.log('Bot server is healthy');
      }
    }, this.healthCheckIntervalTime);
  };

  public disconnect = async (): Promise<void> => {
    console.log('SessionManager::Disconnecting...');
    if (this.healthCheckInterval) {
      window.clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = undefined;
    }

    await this.session?.disconnect();

    try {
      await endBotSession(this.botSessionData.sessionId, this.functionsAppToken);
    } catch (e) {
      console.error('Failed to close bot session', e);
    }

    this.localAudioStream?.dispose();

    this.session?.off('configureRequest', this.configureRequest);
    this.session?.off('stateChanged', this.sessionStateChanged);
    this.session?.off('streamsUpdated', this.streamsUpdated);

    this.session = undefined;
    window.removeEventListener('beforeunload', this.disconnect);
  };

  public mute = async (): Promise<void> => {
    console.log('SessionManager::Muting...');
    await callWithEventTracking(async () => this.session?.mute(), this.appInsights, 'BotSession_Mute');
  };

  public unmute = async (): Promise<void> => {
    console.log('SessionManager::Unmuting...');
    await callWithEventTracking(async () => this.session?.mute(), this.appInsights, 'BotSession_Unmute');
  };

  public on = (event: 'serverError', callback: (...args: unknown[]) => void): void => {
    this.eventEmitter.on(event, callback);
  };
  public off = (event: 'serverError', callback: (...args: unknown[]) => void): void => {
    this.eventEmitter.off(event, callback);
  };
}
